import React, {
    ReactElement, useEffect, useState,
} from "react";
import {
    Dropdown,
} from "react-bootstrap";
import { AiOutlineClose } from "react-icons/ai";
import { VscSettingsGear } from "react-icons/vsc";
import useKeyboardShortcuts, { useKeyboardState } from "../../../contexts/KeyboardShortcutContext";
import usePortal from "../../../contexts/PortalContext";
import useRouting from "../../../contexts/Routing";
import {
    DashboardGroupBy,
    DashboardSettings,
    DashboardSortBy,
    DashboardView,
    Permission,
    SearchFiltersInput,
    TicketPriority,
    TicketStatus,
    TicketView,
} from "../../../__generated__/api";
import Agents from "../../pickers/agents/Agents";
import DateRangeFilter from "../../pickers/date-range/DateRangePicker";
import RequestTypes from "../../pickers/request-types/RequestTypes";
import Users from "../../pickers/users/Users";
import Tags from "../../tags/Tags";
import ManageColumns, {
    GROUP_BY_TEXT, SORT_BY_TEXT_ICONS, VIEW_ICONS, VIEW_TEXT,
} from "../manage-columns/ManageColumns";

interface TopOptionProps {
    updateColumns: (settings?: Partial<DashboardSettings>) => Promise<unknown>;
    refreshColumns: () => Promise<unknown>;
    settings: DashboardSettings;
    search: (text: string) => void;
    onChangeSearch: (searchFilters: SearchFiltersInput) => void
}

export const TICKET_VIEW_TEXT: Record<string, {text: string; permission: Permission; shortcut: string}> = {
    [TicketView.MyTickets]: {
        text: "My Tickets",
        permission: Permission.ChatsRead,
        shortcut: "d+m",
    },
    [TicketView.MyQueues]: {
        text: "My Team's Tickets",
        permission: Permission.ChatsReadAll,
        shortcut: "d+t",
    },
};

export const PRIORITY_TEXT = {
    [TicketPriority.Critical]: "Critical",
    [TicketPriority.High]: "High",
    [TicketPriority.Medium]: "Medium",
    [TicketPriority.Low]: "Low",
};

export const STATUS_TEXT = {
    [TicketStatus.Closed]: "Closed",
    [TicketStatus.Deflection]: "Started Deflection",
    [TicketStatus.Hold]: "On Hold",
    [TicketStatus.Ignored]: "Ignored",
    [TicketStatus.Open]: "Open",
    [TicketStatus.Reopened]: "Reopened",
    [TicketStatus.Snooze]: "Snoozed",
};

interface SearchFillterProps {
    onChange: ((filters: SearchFiltersInput) => void);
    filters: SearchFiltersInput;
}
function SearchFiltersDisplay({ onChange, filters }: SearchFillterProps): ReactElement {
    useEffect(() => {
        onChange(filters);
    }, [filters]);
    return (
        <div className="mx-3 my-1 d-flex justify-content-start">
            <span className="text-light me-3">
                Filter:
            </span>

            <DateRangeFilter
                onChange={(startDate, endDate) => onChange({ ...filters, startDate, endDate })}
            />
            <Dropdown
                className="p-0 m-0 text-light"
            >
                <Dropdown.Toggle variant="none" className="text-light-400 p-0 me-3">
                    Priority
                    {" "}
                    {filters.priority && PRIORITY_TEXT[filters.priority]}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {Object.entries(PRIORITY_TEXT).map(([priority, icon]) => (
                        <Dropdown.Item
                            key={priority}
                            className={filters.priority === priority ? "active" : ""}
                            onClick={() => {
                                onChange({
                                    ...filters,
                                    priority: priority as TicketPriority,
                                });
                            }}
                        >
                            {icon}
                        </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item
                        onClick={() => {
                            onChange({
                                ...filters,
                                priority: undefined,
                            });
                        }}
                    >
                        All
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <Dropdown
                className="p-0 m-0 text-light"
            >
                <Dropdown.Toggle variant="none" className="text-light-400 p-0 me-3">
                    Status
                    {" "}
                    {filters.status && STATUS_TEXT[filters.status]}
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    {Object.entries(STATUS_TEXT).map(([status, text]) => (
                        <Dropdown.Item
                            key={status}
                            className={filters.status === status ? "active" : ""}
                            onClick={() => {
                                onChange({
                                    ...filters,
                                    status: status as TicketStatus,
                                });
                            }}
                        >
                            {text}
                        </Dropdown.Item>
                    ))}
                    <Dropdown.Divider />
                    <Dropdown.Item
                        onClick={() => {
                            onChange({
                                ...filters,
                                status: undefined,
                            });
                        }}
                    >
                        All
                    </Dropdown.Item>
                </Dropdown.Menu>
            </Dropdown>

            <RequestTypes
                isMulti
                shouldShowDropdown
                className="p-0 text-light-400 me-3"
                toggleClassName="text-light-400 p-0 me-3 btn-none"
                value={filters.formIds as string[]}
                toggleText="Request Type"
                onSelected={rows => {
                    onChange({
                        ...filters,
                        formIds: rows,
                    });
                }}
            />

            <Tags
                shouldShowDropdown
                className="p-0 text-light-400 me-3"
                toggleClassName="text-light-400 p-0 me-3 btn-none"
                value={filters.tags as string[]}
                toggleText="Tags"
                onChange={tags => {
                    onChange({
                        ...filters,
                        tags,
                    });
                }}
            />

            <Dropdown
                className="p-0 m-0 text-light col-4"
            >
                <Dropdown.Toggle variant="none" className="text-light-400 p-0 me-3">
                    Users
                </Dropdown.Toggle>
                <Dropdown.Menu>
                    <div
                        className="p-2 py-1 "
                    >
                        <h6 className="mb-3">Filter by Users</h6>

                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="mb-4 w-100">
                            <span className="mb-1">Assigned Agent</span>
                            <Agents
                                isClearable
                                className="p-0 text-light-400 me-3"
                                value={filters.assignedAgentId as string}
                                onSelected={(assignedAgentId: string | undefined) => {
                                    onChange({
                                        ...filters,
                                        assignedAgentId,
                                    });
                                }}
                            />
                        </label>

                        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                        <label className="mb-2 w-100">
                            <span className="mb-1">Creator</span>

                            <Users
                                isClearable
                                className="p-0 text-light-400 me-3"
                                value={filters.creatorId as string}
                                onSelected={creatorId => {
                                    onChange({
                                        ...filters,
                                        creatorId,
                                    });
                                }}
                            />
                        </label>
                    </div>

                </Dropdown.Menu>
            </Dropdown>

        </div>
    );
}

const FLAGS_KEY = "flags";

function filterEmpty(input: Record<string, unknown>): Record<string, unknown> {
    const params = Object.create(null);
    Object.entries(input).forEach(([key, value]) => {
        if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
            params[key] = value;
        }
    });

    return params;
}
function deserializeParams(params: Record<string, unknown>): SearchFiltersInput {
    return filterEmpty({
        ...params,
        startDate: params.startDate ? new Date(params.startDate as string) : undefined,
        endDate: params.endDate ? new Date(params.endDate as string) : undefined,
        formIds: (!params.formIds || Array.isArray(params.formIds)) ? params.formIds : [params.formIds],
        tags: (!params.tags || Array.isArray(params.tags)) ? params.tags : [params.tags],
    } as SearchFiltersInput);
}

export default function TopOptions({
    updateColumns, refreshColumns, settings, search, onChangeSearch,
}: TopOptionProps): ReactElement {
    const { getSearchParams, Paths, historyPushSearch } = useRouting();
    const [shouldShowManageColumns, setShowManageColumns] = useState(false);
    const [dashboardView, setDashboardView] = useState(() => settings?.view ?? DashboardView.Kanban);
    const [sortBy, setSortBy] = useState(() => settings?.sortBy ?? DashboardSortBy.CreatedDateDescending);
    const [groupBy, setGroupBy] = useState(() => settings?.groupBy ?? DashboardGroupBy.User);
    const [ticketView, setTicketView] = useState(() => settings?.ticketView ?? TicketView.MyTickets);
    const [searchValue, setSearchValue] = useState<string>(() => getSearchParams(Paths.AGENT).search ?? "");
    const [filters, setFilters] = useState<SearchFiltersInput>(() => deserializeParams(getSearchParams(Paths.AGENT) ?? {}));

    const keyboardState = useKeyboardState();
    const [flags, setFlags] = useState<Record<string, boolean>>(() => JSON.parse(localStorage.getItem(FLAGS_KEY) ?? "{}"));
    useEffect(() => {
        if (flags) {
            localStorage.setItem(FLAGS_KEY, JSON.stringify(flags));
        }
    }, [flags]);
    const { permissions } = usePortal();
    const [isShowSearch, setIsShowSearch] = useState(false);
    const searchRef = React.createRef<HTMLInputElement>();
    useEffect(() => {
        if (isShowSearch) {
            searchRef.current?.focus();
        }
    }, [isShowSearch, searchRef]);

    useEffect(() => {
        if (!isShowSearch) {
            historyPushSearch({});
            onChangeSearch({});
        }
    }, [isShowSearch]);

    const startSearch = () => {
        search(searchValue);
        setIsShowSearch(true);
        setFlags({ ...flags, "search.button": true });
        keyboardState?.pushContext("search");
    };

    const doneSearch = () => {
        setIsShowSearch(false);
        keyboardState.popContext("search");
        search("");
    };
    useEffect(() => {
        const params = Object.create(null);
        Object.entries({ ...filters, search: searchValue }).forEach(([key, value]) => {
            if (value != null && value !== "" && (!Array.isArray(value) || value.isNotEmpty)) {
                params[key] = value instanceof Date ? value.toISOString() : value;
            }
        });

        if (Object.keys(params).isNotEmpty) {
            search(params.search);
            const copy = { ...filters };
            delete (copy as unknown as {search?: string}).search;
            onChangeSearch(copy);
            startSearch();
        }

        historyPushSearch(params);
    }, [searchValue, filters]);

    const afterUpdateColumns = async () => {
        setShowManageColumns(false);
        await refreshColumns();
    };

    if (ticketView === TicketView.All) {
        setTicketView(TicketView.MyTickets);
    }

    useEffect(() => {
        const values: Partial<DashboardSettings> = {};
        if (settings?.sortBy !== sortBy) {
            values.sortBy = sortBy;
        }
        if (settings?.groupBy !== groupBy) {
            values.groupBy = groupBy;
        }
        if (settings?.ticketView !== ticketView) {
            values.ticketView = ticketView;
        }
        if (settings?.view !== dashboardView) {
            values.view = dashboardView;
        }
        if (Object.keys(values).isNotEmpty) {
            (async () => {
                const copy = {
                    ...(settings ?? {}),
                    ...values,
                };
                delete copy.__typename;
                await updateColumns(copy);
                refreshColumns();
            })();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sortBy, groupBy, ticketView, dashboardView]);

    useEffect(() => {
        if (!settings) {
            return;
        }

        setSortBy(settings.sortBy ?? sortBy);
        setGroupBy(settings.groupBy ?? groupBy);
        setTicketView(settings.ticketView ?? ticketView);
        setDashboardView(settings.view ?? dashboardView);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings?.sortBy, settings?.groupBy, settings?.ticketView, settings?.view]);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const viewableTickets = Object.entries(TICKET_VIEW_TEXT).filter(([_key, props]) => permissions.has(props.permission));
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const selectableGroups = Object.entries(GROUP_BY_TEXT).filter(([_key, props]) => permissions.has(props.permission));

    useKeyboardShortcuts(shortcuts => {
        shortcuts.on({
            group: "Navigation",
            context: "default",
            description: "Search",
            shortcut: "/",
            handler: () => {
                startSearch();
            },
        });
        shortcuts.on({
            shortcut: "esc",
            context: "search",
            handler: () => {
                doneSearch();
            },
        });

        shortcuts.on({
            shortcut: "g+u",
            context: "default",
            description: "Group by user",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.User);
            },
        });
        shortcuts.on({
            shortcut: "g+a",
            context: "default",
            description: "Group by Assignee",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.Assignee);
            },
        });
        shortcuts.on({
            shortcut: "g+c",
            context: "default",
            description: "Group by channel",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.Channel);
            },
        });
        shortcuts.on({
            shortcut: "g+p",
            context: "default",
            description: "Group by Priority",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.Priority);
            },
        });
        shortcuts.on({
            shortcut: "g+t",
            context: "default",
            description: "Group by company or team",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.CompanyGroup);
            },
        });
        shortcuts.on({
            shortcut: "g+q",
            context: "default",
            description: "Group by Queue",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.Queue);
            },
        });
        shortcuts.on({
            shortcut: "g+d",
            context: "default",
            description: "Dont group",
            group: "Grouping",
            handler: async () => {
                setGroupBy(DashboardGroupBy.NoGroup);
            },
        });

        shortcuts.on({
            shortcut: "s+c",
            context: "default",
            description: "Sort by Created Date",
            group: "Sorting",
            handler: async () => {
                setSortBy(value => (value === DashboardSortBy.CreatedDateAscending
                    ? DashboardSortBy.CreatedDateDescending
                    : DashboardSortBy.CreatedDateAscending));
            },
        });
        shortcuts.on({
            shortcut: "s+p",
            context: "default",
            description: "Sort by Priority",
            group: "Sorting",
            handler: async () => {
                setSortBy(value => (value === DashboardSortBy.PriorityAscending
                    ? DashboardSortBy.PriorityDescending
                    : DashboardSortBy.PriorityAscending));
            },
        });
        shortcuts.on({
            shortcut: "s+l",
            context: "default",
            description: "Sort by Last Response",
            group: "Sorting",
            handler: async () => {
                setSortBy(value => (value === DashboardSortBy.LastResponseDateAscending
                    ? DashboardSortBy.LastResponseDateDescending
                    : DashboardSortBy.LastResponseDateAscending));
            },
        });

        shortcuts.on({
            shortcut: "v+k",
            context: "default",
            description: "Display as a Kanban",
            group: "Dashboard View",
            handler: async () => {
                setDashboardView(value => (value === DashboardView.Kanban
                    ? DashboardView.KanbanWrapped
                    : DashboardView.Kanban));
            },
        });

        shortcuts.on({
            shortcut: "v+w",
            context: "default",
            description: "Display as a Wall",
            group: "Dashboard View",
            handler: async () => {
                setDashboardView(DashboardView.Wall);
            },
        });

        viewableTickets.forEach(([key, props]) => {
            shortcuts.on({
                shortcut: props.shortcut,
                description: props.text,
                group: "Display",
                context: "default",
                handler: async () => {
                    setTicketView(key as TicketView);
                },
            });
        });
    });

    if (isShowSearch) {
        return (
            <div className="d-flex flex-column  mt-2">
                <div className="d-flex">
                    <input
                        type="search"
                        className="no-border m-0 w-100 py-1 fs-4 px-4 bg-white flex-grow-1"
                        placeholder="Search"
                        ref={searchRef}
                        value={searchValue}
                        onChange={element => setSearchValue(element.target.value)}
                    />

                    <button type="button" className="btn btn-small border p-0 px-2 mx-2" onClick={() => doneSearch()}>
                        <AiOutlineClose />
                        {" "}
                        <small>
                            Close
                        </small>

                    </button>
                </div>

                <SearchFiltersDisplay onChange={setFilters} filters={filters} />

            </div>
        );
    }

    return (
        <div className="d-flex justify-content-between align-items-center">
            <h4 className="text-gray-500 ms-3 my-2 py-1">
                Conversations
            </h4>

            <div className="me-3 d-flex align-items-center">
                <button type="button" className="btn btn-link me-4 text-light-400 p-0" onClick={() => startSearch()}>
                    <small>
                        Search
                    </small>
                </button>
                {viewableTickets.length > 1 && (
                    <Dropdown className="p-0 m-0 text-light">
                        <Dropdown.Toggle variant="none" className="text-light-400 p-0 me-3">
                            <small>
                                Display:
                                {" "}
                                {TICKET_VIEW_TEXT[ticketView]?.text}
                            </small>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {viewableTickets.map(([value, props]) => (
                                <Dropdown.Item
                                    key={value}
                                    onClick={() => {
                                        setTicketView(value as TicketView);
                                    }}
                                >
                                    {props.text}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                <Dropdown className="p-0 m-0 text-light">
                    <Dropdown.Toggle variant="none" className="text-light-400 p-0 me-3">
                        <small>
                            Group:
                            {" "}
                            {GROUP_BY_TEXT[groupBy].text}
                        </small>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {selectableGroups.map(([key, value]) => (
                            <Dropdown.Item
                                key={key}
                                onClick={() => {
                                    setGroupBy(key as DashboardGroupBy);
                                }}
                            >
                                {value.text}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown className="p-0 m-0 text-light">
                    <Dropdown.Toggle variant="none" className="text-light-400 p-0">
                        <small>
                            {SORT_BY_TEXT_ICONS[sortBy]}
                        </small>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {Object.entries(SORT_BY_TEXT_ICONS).map(([value, text]) => (
                            <Dropdown.Item
                                key={value}
                                onClick={() => {
                                    setSortBy(value as DashboardSortBy);
                                }}
                            >
                                {text}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <Dropdown className="p-0 m-0 text-light">
                    <Dropdown.Toggle variant="none" className="text-light-400 p-0 ms-3">
                        <small>
                            {VIEW_ICONS[dashboardView]}
                        </small>
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                        {Object.entries(VIEW_ICONS).filter(([key]) => key !== DashboardView.Wall).map(([value, text]) => (
                            <Dropdown.Item
                                key={value}
                                onClick={() => {
                                    setDashboardView(value as DashboardView);
                                }}
                            >
                                {text}
                                {" "}
                                {VIEW_TEXT[value as DashboardView]}
                            </Dropdown.Item>
                        ))}
                    </Dropdown.Menu>
                </Dropdown>
                <button type="button" className="btn btn-none" onClick={() => setShowManageColumns(true)}>
                    <VscSettingsGear />
                </button>
                {shouldShowManageColumns && (
                    <ManageColumns onClose={afterUpdateColumns} />
                )}
            </div>

        </div>
    );
}
